import React, { FC, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Ajv from 'ajv';

import { excelDataToApiFormat } from '../../helpers';

interface DataGridValidatorProps {
  schema: any;
  excelData: any;
  shouldValidate: boolean;
  columnLabels: string[] | null;
  onChange?: any;
}

const StyledErrorMessages = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  h4 {
    font-weight: bold;
    color: red;
  }

  li {
    //color: red;
    padding: 1rem 2rem;
    background-color: #ffa7a7;
    margin-bottom: 0.3rem;

    p {
      margin: 0;
    }
  }
`;

//@todo: fix types
const ajv = new Ajv({
  // strictKeywords: false,
  allErrors: true,
});

const mapObjectKeyNames = (keyNamesMap: any, data: any) =>
  Object.keys(data).reduce((acc: any, key: string) => {
    const newKey = keyNamesMap?.[key] || key;
    acc[newKey] = data[key];
    return acc;
  }, {});

export const DataGridValidator: FC<DataGridValidatorProps> = ({
  shouldValidate,
  schema,
  excelData,
  columnLabels,
  onChange,
}) => {
  const schemaProperties = schema?.items?.properties;

  const displayNameToKeyNameMap = schemaProperties
    ? Object.keys(schemaProperties).reduce((acc: any, keyName) => {
        acc[schemaProperties[keyName]?.displayName] = keyName;
        return acc;
      }, {})
    : null;

  // @todo - restrict to number of columns only from schema
  const dataGridErrors = useMemo(() => {
    if (!schema || !excelData || !shouldValidate) {
      return null;
    }

    // let's map keys back to schema ones (from displayNames)
    const data = excelDataToApiFormat(excelData, columnLabels).map((item: any) =>
      mapObjectKeyNames(displayNameToKeyNameMap, item),
    );

    const validate = ajv.compile(schema); // schema
    const valid = validate(data);

    const tableCoords = new Set();

    const messages = (validate?.errors || []).map((errorItem) => {
      //@todo - check if this is working fine with data grids
      //@ts-ignore
      const messageCords = errorItem?.dataPath ? errorItem.dataPath.split('.') : [];

      if (!messageCords?.length) {
        return null;
      }

      const rowNumber = messageCords?.[0]?.match(/\d+/)?.[0];
      const columnKeyName = messageCords[1] || 'Unknown';
      const columnName = schemaProperties?.[columnKeyName]?.displayName || columnKeyName;

      tableCoords.add(`${messageCords[1]}___${columnName}`);

      return {
        title: `Row ${rowNumber} at ${columnName}`,
        message: errorItem.message,
      };
    });

    return {
      isValid: valid,
      messages: messages,
      tableCoords: tableCoords,
    };
  }, [excelData]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const table = document.getElementsByTagName('table');
      if (table && dataGridErrors?.tableCoords) {
        const allErrorCells = document.querySelectorAll('table .error-cell');
        allErrorCells.forEach((cell) => {
          cell.classList.remove('error-cell');
        });

        // if errors add classes to correct cells
        dataGridErrors?.tableCoords.forEach((coord: any) => {
          const [rowIndex, columnName] = coord.split('___');
          const trElement = document.querySelector(`table tr[row="${rowIndex}"]`);

          if (trElement && columnLabels) {
            const columnIndex = columnLabels.findIndex((item) => item == columnName);
            const tdElement = trElement.getElementsByTagName('td')[columnIndex];

            if (tdElement) {
              tdElement.classList.add('error-cell');
            }
          }
        });
      }
    }, 10);

    return () => {
      clearTimeout(timeout);
    };
  }, [dataGridErrors?.tableCoords]);

  useEffect(() => {
    if (onChange && typeof onChange === 'function') {
      onChange({ isValid: dataGridErrors?.isValid });
    }
  }, [dataGridErrors?.isValid]);

  if (dataGridErrors?.isValid || !dataGridErrors?.messages?.length) {
    return null;
  }

  return (
    <StyledErrorMessages>
      <h4>Errors:</h4>

      {dataGridErrors?.messages.map((item: any, index: number) => (
        <li key={index}>
          <p>
            <strong>{item?.title || ''}</strong>: {item?.message || ''}
          </p>
        </li>
      ))}
    </StyledErrorMessages>
  );
};
